import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Popover } from 'components/atoms/Popover';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import {
  extractTimesFromDateTime,
  formatResourceDateLong,
  formatSessionTimeRange,
} from 'utils/dateTime';
import { Session } from 'types/listings';

interface ResourceMetaProps {
  date: string;
  selectedSession?: Session;
  sessions: Session[];
  onSessionSelect: (time: string, duration: number) => void;
}

export const ResourceMeta = ({
  selectedSession,
  date,
  sessions,
  onSessionSelect,
}: ResourceMetaProps) => {
  const { locale } = useGlobalDateLocale();

  const { startTime, endTime } = useMemo(() => {
    if (!selectedSession) {
      return { startTime: '', endTime: '' };
    }
    const { timePart } = extractTimesFromDateTime(
      selectedSession.startDateTime,
    );
    return formatSessionTimeRange(
      timePart.substring(0, 8),
      selectedSession.duration,
      locale,
    );
  }, [selectedSession, locale]);

  const allSessions = useMemo(() => {
    return sessions.map((session) => {
      const { timePart } = extractTimesFromDateTime(
        selectedSession?.startDateTime,
      );
      const { startTime: formattedStartTime, endTime: formattedEndTime } =
        formatSessionTimeRange(
          timePart.substring(0, 8),
          session.duration,
          locale,
        );
      return { ...session, formattedStartTime, formattedEndTime };
    });
  }, [sessions, locale]);

  const formattedDate = useMemo(
    () => formatResourceDateLong(date, locale),
    [date, locale],
  );

  const handleKeyUp = (
    e: React.KeyboardEvent<HTMLDivElement>,
    session: Session,
  ) => {
    const { timePart } = extractTimesFromDateTime(session.startDateTime);
    if (e.key === 'Enter') {
      onSessionSelect(timePart, session.duration);
    }
  };

  return (
    <DateTimeLabel className="resource-detail-meta">
      {formattedDate}&nbsp; • &nbsp;
      {allSessions.length > 1 ? (
        <Popover
          asChild
          showCloseIcon={false}
          trigger={
            <TimeSelector>
              {startTime.toUpperCase()} - {endTime.toUpperCase()}
            </TimeSelector>
          }
        >
          <StyledSessionCard role="menu">
            {allSessions.map((session) => (
              <div
                className={
                  session.startDateTime === selectedSession?.startDateTime &&
                  session.duration === selectedSession.duration
                    ? 'selected-session session-item'
                    : 'session-item'
                }
                tabIndex={0}
                role="menuitem"
                key={session.duration + session.startDateTime}
                onClick={() =>
                  onSessionSelect(
                    session.startDateTime.split('T')[1],
                    session.duration,
                  )
                }
                onKeyUp={(e) => handleKeyUp(e, session)}
              >
                {session.formattedStartTime.toUpperCase()} -{' '}
                {session.formattedEndTime.toUpperCase()}
              </div>
            ))}
          </StyledSessionCard>
        </Popover>
      ) : (
        `${startTime.toUpperCase()} - ${endTime.toUpperCase()}`
      )}
    </DateTimeLabel>
  );
};

const DateTimeLabel = styled.p`
  display: flex;
  font-size: 15px;
  font-weight: 400;
  line-height: 17px;
  color: var(--way-colors-contrastColorShades-100);
  margin: 0px;
`;

const TimeSelector = styled(DateTimeLabel)`
  cursor: pointer;
  text-decoration: underline;
`;

const StyledSessionCard = styled.div`
  width: 195px;
  gap: 8px;
  border-radius: var(--way-design-borderRadiusDefault);
  border: 0.5px solid var(--way-colors-borderColor);
  background: var(--way-palette-white-100);

  > * {
    width: 100%;
    padding: 12px 16px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    line-height: 15px;

    &:hover {
      background: var(--way-palette-black-10);
    }

    &.selected-session {
      background: var(--way-palette-black-20);
      &:hover {
        background: var(--way-palette-black-30);
      }
    }
  }
`;
